import {useCallback, useEffect, useRef, useState} from 'react';
import ArrDataGrid from '../common/arr-data-grid/ArrDataGrid';
import {
  backgroundTasksUtils,
  BackgroundTaskTypes,
  checkIsItLink,
  checkIsItViewable,
} from './backgroundTasksUtils';
import {GridSortModel} from '@mui/x-data-grid';
import {
  useBackgroundTasks,
  useBackgroundTaskStatuses,
} from '../../hooks/backgroundTasks/useBackgroundTasks';
import {t} from 'i18next';
import ArrSingleSelectDropdown from '../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  IBackgroundTask,
  IBackgroundTaskStatus,
  IBackgroundTaskType,
} from '../../api/types/backgroundTask.type';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import './BackgroundTasks.scss';
import ArrButton from '../common/arr-button/ArrButton';
import {ISelectItem} from '../../shared/types';
import {ReactComponent as FolderIcon} from '../../assets/images/no-data-found.svg';
import {ToastType} from '../../shared/constants';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import dayjs from 'dayjs';

const ROW_HEIGHT = 36;

const visuallyHiddenSx = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

const BackgroundTasks = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedStatus, setSelectedStatus] =
    useState<IBackgroundTaskStatus | null>(null);
  const [selectedTaskType, setSelectedTaskType] =
    useState<IBackgroundTaskType | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'documentName',
      sort: 'desc',
    },
  ]);
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    taskType: '',
  });
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [liveMessage, setLiveMessage] = useState('');

  const pageTitleRef = useRef<HTMLHeadingElement | null>(null);

  const onPageChangeHandler = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const handleErrorToast = (val: boolean = false) => {
    setShowErrorToast(val);
  };

  const payload = {
    filterStatus: appliedFilters.status || '',
    taskType: appliedFilters.taskType || '',
    direction: sortModel[0]?.sort || 'asc',
    sortingColumn: sortModel[0]?.field || 'documentName',
    pageNumber,
    pageSize: itemsPerPage,
  };

  const handleApplyFilters = () => {
    setPageNumber(1);
    setAppliedFilters({
      status:
        selectedStatus?.value && selectedStatus?.value != '-1'
          ? selectedStatus?.value
          : '',
      taskType: selectedTaskType?.value ? selectedTaskType?.value : '',
    });
    setLiveMessage(t('backgroundTasks.filters.apply'));
  };

  const {data: backgroundTasks, isLoading: isBackgroundTasksLoading} =
    useBackgroundTasks({
      ...payload,
    });

  const {data: backgroundTaskStatuses} = useBackgroundTaskStatuses();

  const rows = backgroundTasks?.aaData?.map(
    (row: IBackgroundTask, index: number) => {
      const typesToShowNA = [
        'BACKGROUNDREPORT',
        'EXPORTREPORT',
        'SHAREREPORT',
        'REPORTSCHEDULE',
      ];
      return {
        ...row,
        id: index,
        actualDocs: typesToShowNA.includes(row?.notificationTypeCode)
          ? 'N/A'
          : row.actualDocs,
        startDate: row.startDate
          ? dayjs(row.startDate).format('YYYY/MM/DD HH:mm:ss')
          : ' ',
        endDate: row.endDate
          ? dayjs(row.endDate).format('YYYY/MM/DD HH:mm:ss')
          : ' ',
        isLink: checkIsItLink(row),
        isViewable: checkIsItViewable(row),
        downloadFail: () => handleErrorToast(true),
      };
    }
  );

  useEffect(() => {
    if (isBackgroundTasksLoading) {
      setLiveMessage(t('common.loading'));
      return;
    }
    const totalRecords = backgroundTasks?.totalRecords ?? 0;
    if (totalRecords === 0) {
      setLiveMessage(t('backgroundTasks.messageForNoGridData'));
      return;
    }
    setLiveMessage(`${totalRecords} ${t('backgroundTasks.entity')} loaded`);
  }, [backgroundTasks?.totalRecords, isBackgroundTasksLoading]);

  useEffect(() => {
    if (showErrorToast) {
      setLiveMessage(t('backgroundTasks.downloadError'));
    }
  }, [showErrorToast]);

  useEffect(() => {
    requestAnimationFrame(() => {
      pageTitleRef.current?.focus();
    });
  }, []);

  const backgroundTaskStatusOptions = backgroundTaskStatuses?.map(
    (item: any, index: number) => ({
      key: index,
      value: item.statusTypeCode,
      text:
        item.statusTypeLabel
          ?.toLowerCase()
          .replace(/\b\w/g, (char: string) => char.toUpperCase()) || null,
    })
  );

  const NoRowsOverlay = useCallback(
    () => (
      <Box
        className="no-grid-overlay"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="background-tasks-message-container-for-no-grid-data"
      >
        <FolderIcon
          aria-hidden="true"
          focusable="false"
          data-testid="background-tasks-no-grid-data-folder-icon"
        />
        <Typography
          className="typo-text headline3_size"
          data-testid={`background-tasks-message-for-no-grid-data`}
        >
          {t('backgroundTasks.messageForNoGridData')}
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <>
      <Box
        component="section"
        className="view-background-tasks-container"
        aria-labelledby="background-tasks-page-title"
        data-testid="background-tasks-container"
      >
        <Box
          component="header"
          className="view-background-tasks-header"
          data-testid="background-tasks-header"
        >
          <Box
            className="view-background-tasks-title"
            data-testid="background-tasks-title-container"
          >
            <Typography
              ref={pageTitleRef}
              tabIndex={0}
              id="background-tasks-page-title"
              component="h1"
              className="typo-text headline3_size view-background-tasks-title-text"
              data-testid="background-tasks-title"
            >
              {t('backgroundTasks.title')}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={visuallyHiddenSx}
          aria-live="polite"
          aria-atomic="true"
          data-testid="background-tasks-live-message"
        >
          {liveMessage}
        </Box>
        <Box
          component="section"
          className="filter-panel"
          aria-labelledby="background-tasks-filters-title"
          data-testid="background-tasks-filter-panel"
        >
          <Box className="filters" data-testid="background-tasks-filters">
            <Box
              className="panel-header"
              data-testid="background-tasks-filter-panel-header"
            >
              <Typography
                id="background-tasks-filters-title"
                component="h2"
                className="typo-text subhead2_size title-label"
                data-testid={`background-tasks-filter-by`}
              >
                {t('backgroundTasks.filters.filterBy')}
              </Typography>
            </Box>
            <Box
              className="filter-section"
              data-testid="background-tasks-filter-section"
            >
              <Box
                className="filter-item"
                role="group"
                aria-labelledby="background-tasks-status-label"
                data-testid="background-tasks-status-filter"
              >
                <Typography
                  id="background-tasks-status-label"
                  component="span"
                  className="filter-label"
                  data-testid={`background-tasks-status`}
                >
                  {t('backgroundTasks.filters.status')}
                </Typography>
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'backgroundTasks.filters.dropdownDefaultValue'
                  )}
                  dropdownItems={backgroundTaskStatusOptions ?? []}
                  selectedValues={selectedStatus as unknown as ISelectItem}
                  onSelectionChange={(value) =>
                    setSelectedStatus(value as unknown as IBackgroundTaskStatus)
                  }
                  sizeVariant="medium"
                  dropdownItemKey="key"
                  dropdownItemDisplay="text"
                  customWidth={25}
                  dataTestId="select-status-label"
                />
              </Box>
              <Box
                className="filter-item"
                role="group"
                aria-labelledby="background-tasks-task-type-label"
                data-testid="background-tasks-task-type-filter"
              >
                <Typography
                  id="background-tasks-task-type-label"
                  component="span"
                  className="filter-label"
                  data-testid="background-tasks-task-type"
                >
                  {t('backgroundTasks.filters.taskType')}
                </Typography>
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'backgroundTasks.filters.dropdownDefaultValue'
                  )}
                  dropdownItems={BackgroundTaskTypes ?? []}
                  selectedValues={selectedTaskType as unknown as ISelectItem}
                  onSelectionChange={(value) =>
                    setSelectedTaskType(value as unknown as IBackgroundTaskType)
                  }
                  sizeVariant="medium"
                  dropdownItemKey="key"
                  dropdownItemDisplay="text"
                  customWidth={25}
                  dataTestId="task-type-label"
                />
              </Box>
              <Box className="filter-item">
                <ArrButton
                  size="medium"
                  onClick={handleApplyFilters}
                  data-test-id="background-tasks-apply-filters-btn"
                  aria-label={t('backgroundTasks.filters.apply')}
                  dataTestId="background-tasks-apply-button"
                >
                  {t('backgroundTasks.filters.apply')}
                </ArrButton>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          component="section"
          className="background-tasks-grid"
          data-testid="background-tasks-table"
          role="region"
          aria-labelledby="background-tasks-grid-title"
          aria-busy={isBackgroundTasksLoading}
        >
          <Typography
            id="background-tasks-grid-title"
            data-testid="background-tasks-grid-title-hidden"
            sx={visuallyHiddenSx}
          >
            {t('backgroundTasks.title')} {t('backgroundTasks.entity')}
          </Typography>
          <ArrDataGrid
            rowHeight={ROW_HEIGHT}
            rows={rows || []}
            columns={backgroundTasksUtils}
            rowCount={backgroundTasks?.totalRecords || 0}
            currentPage={pageNumber}
            itemsPerPage={itemsPerPage}
            pageSize={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            paginationMode="server"
            onPageChange={onPageChangeHandler}
            entityType={t('backgroundTasks.entity')}
            getRowId={(row) => row.id}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            className="view-background-tasks-grid"
            loading={isBackgroundTasksLoading}
            showPerPageOptions={true}
            pageSizeOptions={[25, 50, 100]}
            noRowsOverlay={NoRowsOverlay}
            sortingMode="server"
            disableVirtualization={true}
          />
        </Box>
      </Box>
      {showErrorToast && (
        <ArrToastNotification
          message={t('backgroundTasks.downloadError')}
          onClose={() => setShowErrorToast(false)}
          status={[ToastType.Error]}
          data-testid="toast-message"
        />
      )}
    </>
  );
};

export default BackgroundTasks;
